import { useEffect, useState } from "react";
import { Menu, Sun, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { NAV_LINKS, BRAND } from "@/data/content";
import { useQuote } from "@/context/QuoteContext";

const Logo = () => (
  <a 
    href="#home" 
    onClick={(e) => {
      e.preventDefault();
      const target = document.querySelector("#home");
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    }}
    className="flex items-center shrink-0" 
    data-testid="nav-logo"
  >
    <img src={`${process.env.PUBLIC_URL}/logo.jpeg`} alt="Logo" className="h-20 w-auto max-w-[240px] object-contain mix-blend-multiply" />
  </a>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass shadow-soft" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-shell mx-auto px-4 md:px-8 lg:px-12 h-[88px] flex items-center justify-between gap-4">
        <Logo />

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => {
                if (l.href.startsWith("#")) {
                  e.preventDefault();
                  const target = document.querySelector(l.href);
                  if (target) {
                    target.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-semibold text-slate-600 hover:text-brand-green transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-brand-green hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => openQuote("navbar")}
            data-testid="nav-cta-quote"
            className="hidden sm:inline-flex h-11 rounded-full bg-brand-yellow text-brand-navy font-semibold hover:bg-[#f0bd1f] hover:-translate-y-0.5 transition-transform shadow-soft px-6"
          >
            Get Free Quote
          </Button>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden rounded-full" data-testid="nav-mobile-toggle">
                <Menu className="h-6 w-6 text-brand-navy" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0">
              <div className="px-6 py-6 border-b">
                <Logo />
              </div>
              <div className="flex flex-col px-4 py-4">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.label}>
                    <a
                      href={l.href}
                      onClick={(e) => {
                        if (l.href.startsWith("#")) {
                          e.preventDefault();
                          const target = document.querySelector(l.href);
                          if (target) {
                            target.scrollIntoView({ behavior: 'smooth' });
                          }
                        }
                      }}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                      className="px-3 py-3 rounded-xl text-base font-semibold text-brand-navy hover:bg-brand-grey transition-colors"
                    >
                      {l.label}
                    </a>
                  </SheetClose>
                ))}
                <Button
                  onClick={() => openQuote("mobile-menu")}
                  data-testid="mobile-nav-cta"
                  className="mt-4 h-12 rounded-full bg-brand-yellow text-brand-navy font-semibold hover:bg-[#f0bd1f]"
                >
                  Get Free Quote
                </Button>
                <a href={`tel:${BRAND.phoneRaw}`} className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-brand-green">
                  <Phone className="h-4 w-4" /> {BRAND.phone}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
